import useAsyncThunkWithSnackbar from '@/app/hooks/useAsyncThunkWithSnackbar';
import TranslationHelper from '@/app/helpers/TranslationHelper';
import {
    createDevice,
    updateDevice,
    updateDevices,
} from '@/app/state/ui/devices';
import { IRestPostDevice, TRestPutDevice } from '@/app/services/devices';
import { TDeviceDialogType, TDeviceFormValues } from '../types';
import { mapCreateDeviceFormValuesToPayload } from '../helpers/mapCreateDeviceFormValuesToPayload';
import { useSelectedMultiDevices } from '@/app/state/ui/devices/index.hooks';
import { usePrivileges } from '@/app/state/auth/index.hooks';

export const useDeviceSave = (
    mode: TDeviceDialogType,
    handleClose: () => void
) => {
    const handleAsyncRequest = useAsyncThunkWithSnackbar();
    const selectedDevices = useSelectedMultiDevices();
    const privileges = usePrivileges();
    const handleSave = async (values: TDeviceFormValues) => {
        const payload = mapCreateDeviceFormValuesToPayload(
            values,
            mode,
            privileges
        );

        const devicesToChange = selectedDevices.map((deviceEl) => ({
            id: deviceEl.id,
            attributes: { eToll: values.advanced.eToll.enabled },
        }));

        const actions: Record<TDeviceDialogType, () => any> = {
            create: () => createDevice(payload as IRestPostDevice),
            edit: () => updateDevice(payload as TRestPutDevice),
            editMultiple: () => updateDevices(devicesToChange),
        };

        const successMessages: Record<TDeviceDialogType, string> = {
            create: TranslationHelper.translate('Device created'),
            edit: TranslationHelper.translate('Device updated'),
            editMultiple: TranslationHelper.translate('Devices updated'),
        };

        const errorMessages: Record<TDeviceDialogType, string> = {
            create: TranslationHelper.translate('Device could not be created'),
            edit: TranslationHelper.translate('Device could not be updated'),
            editMultiple: TranslationHelper.translate(
                'Devices could not be updated'
            ),
        };

        await handleAsyncRequest({
            asyncAction: actions[mode]?.(),
            onSuccess: {
                callback: () => handleClose(),
                message: successMessages[mode],
            },
            onError: {
                callback: () => {
                    if (mode === 'editMultiple') {
                        handleClose();
                    }
                },
                uniqueCase: {
                    409: TranslationHelper.translate('device.dupedId'),
                },
                message: errorMessages[mode],
            },
        });
    };

    return { handleSave };
};
